// src/screens/AnalyticsBoostScreen.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Screen from '../Screen';
import api from '../api';

import backIcon from '../assets/backspace-icon.png';
import boostIcon from '../assets/boost-icon.png';
import downIcon from '../assets/dropdown-icon.png';

// ✅ Official icons (same style as AnalyticsPostPreviewCard)
import likeIconRed from '../assets/like-icon-red.png';
import commentIcon from '../assets/comment-icon.png';
import repostIcon from '../assets/repost-icon.png';
import saveIconFilled from '../assets/save-icon-filled.png';
import tipIcon from '../assets/tip-icon.png'; // kept for future consistency

import AnalyticsPostPreviewCard from '../components/AnalyticsPostPreviewCard';
import DateRangeDrawer, { labelForRange } from '../components/popups/DateRangeDrawer';

import './AnalyticsBoostScreen.css';

function safeNum(n) {
  const x = Number(n);
  return Number.isFinite(x) ? x : 0;
}

function safeArr(a) {
  return Array.isArray(a) ? a : [];
}

function fmtPi(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return '0';
  return x.toFixed(6).replace(/\.?0+$/, '');
}

function fmtDateShort(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '—';
  return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
}

export default function AnalyticsBoostScreen({ user }) {
  const navigate = useNavigate();

  const [range, setRange] = useState('7d'); // 'today' | '7d' | '30d' | 'all'
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [loading, setLoading] = useState(true);
  const [blocked, setBlocked] = useState(null); // { planId, message }
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  const title = 'Boost';
  const rangeLabel = useMemo(() => labelForRange(range), [range]);

  const goBack = () => {
    if (window.history.length > 1) return navigate(-1);
    return navigate('/analytics');
  };

  const load = async (nextRange) => {
    setLoading(true);
    setError('');
    setBlocked(null);

    try {
      const res = await api.get(`/analytics/me/boost?range=${nextRange}`);
      setData(res?.data || null);
    } catch (e) {
      const status = e?.response?.status;
      const payload = e?.response?.data;

      if (status === 403 && payload?.code === 'ANALYTICS_NOT_ALLOWED') {
        setBlocked({
          planId: payload?.planId || 'FREE',
          message:
            payload?.message ||
            'Analytics is available on Premium and Premium+ plans.',
        });
        setData(null);
      } else if (status === 401) {
        setError('Please login again.');
      } else {
        setError(payload?.message || 'Failed to load boost analytics.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      await load(range);
      if (!alive) return;
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);

  const boost = data?.boost || {};

  const boostUsedCount = safeNum(boost.boostUsedCount);
  const boostActiveCount = safeNum(boost.boostActiveCount);
  const lastBoostedAt = boost.lastBoostedAt;

  const stakedPostsCount = safeNum(boost.stakedPostsCount);
  const totalActiveStakedPi = fmtPi(boost.totalActiveStakedPi);

  const mostStakedPost = boost.mostStakedPost || null;
  const topBoostedPosts = safeArr(boost.topBoostedPosts);
  const topStakedPosts = safeArr(boost.topStakedPosts);

  const openPost = (postId) => {
    if (!postId) return;
    navigate(`/post/${postId}`);
  };

  // helper: show engagement icons row using the post preview card numbers
  const EngagementMiniRow = ({ post }) => {
    if (!post) return null;

    const likes = safeNum(post.likes);
    const comments = safeNum(post.comments);
    const shares = safeNum(post.shares);
    const saves = safeNum(post.saves);

    return (
      <div className="ab-miniMetrics">
        <div className="ab-miniMetric">
          <img src={likeIconRed} alt="" className="ab-icon" />
          <span>{likes}</span>
        </div>
        <div className="ab-miniMetric">
          <img src={commentIcon} alt="" className="ab-icon" />
          <span>{comments}</span>
        </div>
        <div className="ab-miniMetric">
          <img src={repostIcon} alt="" className="ab-icon" />
          <span>{shares}</span>
        </div>
        <div className="ab-miniMetric">
          <img src={saveIconFilled} alt="" className="ab-icon" />
          <span>{saves}</span>
        </div>
      </div>
    );
  };

  return (
    <Screen>
      <div className="ab-container">
        <div className="ab-topbar">
          <img src={backIcon} alt="Back" className="ab-back" onClick={goBack} />
          <h2>{title}</h2>
          <div className="ab-spacer" />
        </div>

        {/* ✅ Range dropdown pill */}
        <div className="ab-rangeRow">
          <button
            type="button"
            className="ab-rangeBtn"
            onClick={() => setDrawerOpen(true)}
            aria-label="Change date range"
            disabled={loading}
          >
            <span className="ab-rangeText">{rangeLabel}</span>
            <img
              src={downIcon}
              alt=""
              className="ab-rangeIcon"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          </button>
        </div>

        {loading && (
          <div className="ab-loader">
            <div className="ab-spinner" />
            <p>Loading…</p>
          </div>
        )}

        {!loading && blocked && (
          <div className="ab-card ab-blocked">
            <h3>Premium feature</h3>
            <p>{blocked.message}</p>
            <div className="ab-blocked-row">
              <span className="ab-pill">Your plan: {blocked.planId}</span>
              <button className="ab-upgrade" onClick={() => navigate('/plans')}>
                Upgrade
              </button>
            </div>
          </div>
        )}

        {!loading && !blocked && error && (
          <div className="ab-card ab-error">
            <h3>Couldn’t load</h3>
            <p>{error}</p>
            <button className="ab-upgrade" onClick={() => load(range)}>
              Retry
            </button>
          </div>
        )}

        {!loading && !blocked && !error && (
          <div className="ab-scroll">
            {/* ✅ Explainer */}
            <div className="ab-card ab-note">
              <div className="ab-noteHead">
                <img src={boostIcon} alt="" className="ab-noteIcon" />
                <div>
                  <div className="ab-noteTitle">Visibility tools (not earnings)</div>
                  <div className="ab-noteText">
                    Boosts are your plan promotions. Stakes are community promotions on your posts.
                  </div>
                </div>
              </div>
            </div>

            {/* ✅ Summary grid */}
            <div className="ab-grid">
              <div className="ab-stat">
                <div className="ab-stat-label">Boost used</div>
                <div className="ab-stat-value">{boostUsedCount}</div>
                <div className="ab-stat-sub">
                  Active: <b>{boostActiveCount}</b>
                </div>
              </div>

              <div className="ab-stat">
                <div className="ab-stat-label">Last boost</div>
                <div className="ab-stat-value ab-small">{fmtDateShort(lastBoostedAt)}</div>
                <div className="ab-stat-sub">Most recent boosted post</div>
              </div>

              <div className="ab-stat">
                <div className="ab-stat-label">Stake interest</div>
                <div className="ab-stat-value">{totalActiveStakedPi} Pi</div>
                <div className="ab-stat-sub">
                  Posts with active stake: <b>{stakedPostsCount}</b>
                </div>
              </div>
            </div>

            {/* ✅ Most staked post */}
            <div className="ab-card">
              <div className="ab-card-head">
                <h3>Most staked post</h3>
              </div>

              {!mostStakedPost ? (
                <div className="ab-empty">No staked posts found for this range.</div>
              ) : (
                <div className="ab-highlight">
                  <div className="ab-highlightLeft">
                    <div className="ab-highlightTitle">Active stake</div>
                    <div className="ab-highlightValue">
                      {fmtPi(mostStakedPost.stakeActivePi)} Pi
                    </div>
                    <div className="ab-highlightSub">
                      Last staked:{' '}
                      {mostStakedPost.stakeLastStakedAt
                        ? fmtDateShort(mostStakedPost.stakeLastStakedAt)
                        : '—'}
                    </div>

                    <EngagementMiniRow post={mostStakedPost} />

                    <button
                      className="ab-openBtn"
                      onClick={() => openPost(mostStakedPost.postId)}
                    >
                      Open post
                    </button>
                  </div>

                  <div className="ab-highlightCard">
                    <AnalyticsPostPreviewCard
                      post={mostStakedPost}
                      onClick={() => openPost(mostStakedPost.postId)}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* ✅ Top boosted posts row */}
            <div className="ab-section">
              <div className="ab-section-head">
                <h3>Top boosted posts</h3>
                <span className="ab-count">{topBoostedPosts.slice(0, 5).length}</span>
              </div>

              {topBoostedPosts.length === 0 ? (
                <div className="ab-empty">No boosted posts found for this range.</div>
              ) : (
                <div className="ab-hscroll">
                  {topBoostedPosts.slice(0, 5).map((p) => (
                    <AnalyticsPostPreviewCard
                      key={p.postId}
                      post={p}
                      onClick={() => openPost(p.postId)}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* ✅ Top staked posts row */}
            <div className="ab-section">
              <div className="ab-section-head">
                <h3>Top staked posts</h3>
                <span className="ab-count">{topStakedPosts.slice(0, 5).length}</span>
              </div>

              {topStakedPosts.length === 0 ? (
                <div className="ab-empty">No staked posts found for this range.</div>
              ) : (
                <div className="ab-hscroll">
                  {topStakedPosts.slice(0, 5).map((p) => (
                    <AnalyticsPostPreviewCard
                      key={p.postId}
                      post={p}
                      onClick={() => openPost(p.postId)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ✅ Drawer */}
        <DateRangeDrawer
          open={drawerOpen}
          value={range}
          onClose={() => setDrawerOpen(false)}
          onChange={(v) => setRange(v)}
          title="Date ranges"
        />
      </div>
    </Screen>
  );
}
